import { t } from "@lingui/macro";
import { CopySimple, Info } from "@phosphor-icons/react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Label,
  Switch,
  Tooltip,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { AnimatePresence, motion } from "framer-motion";

import { useResumeStatistics } from "@/client/services/resume";

import { SectionHeader } from "@/client/pages/builder/_components/SectionHeader";

import { useToast } from "@/client/hooks/use-toast";
import { useUser } from "@/client/services/user";
import { useResumeStore } from "@/client/stores/resume";

export const StatisticsSection = () => {
  const id = useResumeStore((state) => state.resume.id);
  const isPublic = useResumeStore((state) => state.resume.visibility === "public");

  const { statistics } = useResumeStatistics(id, isPublic);

  const { user } = useUser();
  const { toast } = useToast();
  const username = user?.username;

  const setValue = useResumeStore((state) => state.setValue);
  const slug = useResumeStore((state) => state.resume.slug);

  // Constants
  const url = `${window.location.origin}/${username}/${slug}`;

  const onCopy = async () => {
    await navigator.clipboard.writeText(url);

    toast({
      variant: "success",
      title: t`A link has been copied to your clipboard.`,
      description: t`Anyone with this link can view and download the resume. Share it on your profile or with recruiters.`,
    });
  };

  return (
    <section id="statistics" className="grid gap-y-5">
      <main className="grid grid-cols-2">
        <div>
          <h3
            className={cn(
              "text-center text-4xl font-bold blur-none transition-all",
              !isPublic && "blur-sm",
            )}
          >
            {statistics?.views ?? 0}
          </h3>
          <p className="text-center opacity-75">{t`Views`}</p>
        </div>

        <div>
          <h3
            className={cn(
              "text-center text-4xl font-bold blur-none transition-all",
              !isPublic && "blur-sm",
            )}
          >
            {statistics?.downloads ?? 0}
          </h3>
          <p className="text-center opacity-75">{t`Downloads`}</p>
        </div>
      </main>

      <div className="space-y-1.5">
        <div className="bg-background/40 flex flex-col items-center gap-y-3 rounded-lg p-2">
          <div className="flex w-full items-center gap-x-4 p-1">
            <Switch
              id="visibility"
              checked={isPublic}
              onCheckedChange={(checked) => {
                setValue("visibility", checked ? "public" : "private");
              }}
            />
            <div>
              <Label htmlFor="visibility" className="space-y-1">
                <strong className="text-md">{t`Public`}</strong>
                <p className="text-xs opacity-60">{t`Link allows view & download.`}</p>
              </Label>
            </div>
          </div>
          <AnimatePresence presenceAffectsLayout>
            {isPublic ? (
              <motion.div
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full"
              >
                <div className="bg-secondary/10 text-primary flex flex-col gap-y-2 rounded-md p-3">
                  <div className="flex items-center gap-x-4">
                    <Label htmlFor="resume-url">{t`URL`}</Label>

                    <div className="flex w-full gap-x-1.5">
                      <Input
                        readOnly
                        id="resume-url"
                        disabled
                        value={url}
                        className="bg-background/50 flex-1"
                      />

                      <Tooltip content={t`Copy to Clipboard`}>
                        <Button
                          className="bg-background/50 text-primary hover:bg-background/80"
                          size="icon"
                          onClick={onCopy}
                        >
                          <CopySimple size={20} />
                        </Button>
                      </Tooltip>
                    </div>
                  </div>

                  <p className="text-primary/70 text-xs">
                    {t`Share this link to view and download the resume.`}
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                className="col-span-2 w-full"
                initial={{ opacity: 0, y: -10, filter: "blur(10px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -50, filter: "blur(10px)" }}
              >
                <div className="bg-secondary/10 text-primary flex items-center gap-x-4 rounded-md p-3 pl-4">
                  <Info size={24} />
                  <div className="flex flex-col">
                    <strong className="text-sm">{t`Track views and downloads.`}</strong>
                    <p className="text-xs leading-relaxed">
                      {t`Statistics are available only for public resumes.`}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
